import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { fetchProfile, fetchWorkersProfiles, patchWorkerAvailableCredits, patchWorkerProfileImage } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { ProfileAvatarCropPanel } from '../components/ProfileAvatarCropPanel';
import { ProfileAvatarImage } from '../components/ProfileAvatarImage';
import pageThemeStyles from '../components/menuPageTheme.module.css';
import styles from './BusinessClientWorkersPage.module.css';
import { useI18n } from '../i18n';
import { localizeAssetFieldLabel } from '../i18n/assetFieldLabels';
import { formatIsraeliPhoneDisplay } from '../utils/phoneE164';
import {
  type BusinessClientWorkerListItem,
  buildBusinessClientEmployerContext,
  parseBusinessClientWorkersList,
  workerListPrimaryLabel,
  workerListSecondaryLabel,
} from '../utils/businessClientWorkerDisplay';
import {
  extractProfileDisplaySummary,
  formatProfileDateOfBirthDisplay,
  resolveProfileRecord,
} from '../utils/profileDisplaySummary';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import { normalizeWorkerAvailableCreditsInput } from '../utils/businessClientWorkerCredits';
import { extractWorkerProfileImageFromMutation } from '../utils/businessClientWorkerProfileImage';
import { persistWorkerAvatarByEmail } from '../utils/workerAvatarByEmailStorage';
import { persistWorkerListAvatarSrc } from '../utils/workerListAvatarStorage';
import { wirePathToImageDisplaySrc } from '../utils/assetImageResolve';
import { assertProfileMutationSuccess, filterWorkersPayloadByActive } from '../utils/profileWorkerList';

type BusinessClientWorkersPageProps = {
  onBack: () => void;
  businessId?: string | null;
  businessName?: string | null;
};

function workerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

function formatCredits(credits: number | null | undefined, locale: string): string {
  if (credits === null || credits === undefined || !Number.isFinite(credits)) {
    return '—';
  }
  return credits.toLocaleString(locale === 'he' ? 'he-IL' : 'en-US', {
    maximumFractionDigits: 2,
  });
}

export function BusinessClientWorkersPage({
  onBack,
  businessId = null,
  businessName = null,
}: BusinessClientWorkersPageProps) {
  const { t, language } = useI18n();
  const { userName } = useAuth();
  const creditsInputId = useId();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const textDir = language === 'he' ? 'rtl' : 'ltr';

  const [workers, setWorkers] = useState<BusinessClientWorkerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [editingCredits, setEditingCredits] = useState(false);
  const [creditsDraft, setCreditsDraft] = useState('');
  const [creditsSaving, setCreditsSaving] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const loadWorkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loginIdentifier = getStoredLoginIdentifier();
      const profileData = await fetchProfile();
      const profileRecord = resolveProfileRecord(profileData);
      const profileSummary = extractProfileDisplaySummary(
        profileData,
        userName,
        loginIdentifier,
        getSessionToken(),
      );
      const employerContext = buildBusinessClientEmployerContext(profileRecord, {
        businessId: businessId?.trim() || profileSummary.businessId,
        contactFirst: profileSummary.firstName,
        contactLast: profileSummary.lastName,
        email: profileSummary.email,
        loginIdentifier,
      });

      const raw = await fetchWorkersProfiles();
      const activeWorkers = filterWorkersPayloadByActive(raw, false);
      setWorkers(parseBusinessClientWorkersList(activeWorkers, employerContext));
    } catch (err) {
      setWorkers([]);
      setError(
        err instanceof AssetRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.somethingWrong'),
      );
    } finally {
      setLoading(false);
    }
  }, [businessId, t, userName]);

  useEffect(() => {
    void loadWorkers();
  }, [loadWorkers]);

  const selectedWorker = useMemo(
    () => workers.find((worker) => worker.id === selectedWorkerId) ?? null,
    [selectedWorkerId, workers],
  );

  const openWorker = useCallback((workerId: string) => {
    setSelectedWorkerId(workerId);
    setEditingCredits(false);
    setCreditsDraft('');
    setAvatarCropFile(null);
    setNotice(null);
  }, []);

  const backToList = useCallback(() => {
    setSelectedWorkerId(null);
    setEditingCredits(false);
    setCreditsDraft('');
    setAvatarCropFile(null);
    setNotice(null);
  }, []);

  const onAvatarPick = useCallback(() => {
    avatarInputRef.current?.click();
  }, []);

  const onAvatarFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    setAvatarCropFile(file);
    setNotice(null);
  }, []);

  const onAvatarCropCancel = useCallback(() => {
    setAvatarCropFile(null);
  }, []);

  const onAvatarCropSave = useCallback(
    async (dataUrl: string) => {
      if (!selectedWorker) {
        setAvatarCropFile(null);
        return;
      }
      setAvatarCropFile(null);
      setAvatarSaving(true);
      setNotice(null);
      try {
        const response = await patchWorkerProfileImage(selectedWorker.id, dataUrl);
        assertProfileMutationSuccess(response, t('profile.businessWorkers.updatePhoto'));
        const serverImage = extractWorkerProfileImageFromMutation(response);
        const displaySrc =
          wirePathToImageDisplaySrc(serverImage ?? dataUrl) ?? serverImage ?? dataUrl;
        persistWorkerListAvatarSrc(selectedWorker.id, displaySrc);
        if (selectedWorker.email) {
          persistWorkerAvatarByEmail(selectedWorker.email, displaySrc);
        }
        setWorkers((current) =>
          current.map((worker) =>
            worker.id === selectedWorker.id ? { ...worker, avatarSrc: displaySrc } : worker,
          ),
        );
        setNotice(t('profile.businessWorkers.photoUpdated'));
        void loadWorkers();
      } catch (err) {
        setNotice(
          err instanceof AssetRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('common.somethingWrong'),
        );
      } finally {
        setAvatarSaving(false);
      }
    },
    [selectedWorker, t, loadWorkers],
  );

  const startEditCredits = useCallback(() => {
    if (!selectedWorker) {
      return;
    }
    const current = selectedWorker.availableCredits;
    setCreditsDraft(current !== null && Number.isFinite(current) ? String(current) : '');
    setEditingCredits(true);
    setNotice(null);
  }, [selectedWorker]);

  const saveCredits = useCallback(async () => {
    if (!selectedWorker) {
      return;
    }
    const amount = normalizeWorkerAvailableCreditsInput(creditsDraft);
    if (amount === null) {
      setNotice(t('profile.businessWorkers.errorInvalidCredits'));
      return;
    }
    setCreditsSaving(true);
    setNotice(null);
    try {
      const response = await patchWorkerAvailableCredits(selectedWorker.id, amount);
      assertProfileMutationSuccess(response, t('profile.businessWorkers.updateCredits'));
      setWorkers((current) =>
        current.map((worker) =>
          worker.id === selectedWorker.id ? { ...worker, availableCredits: amount } : worker,
        ),
      );
      setEditingCredits(false);
      setNotice(t('profile.businessWorkers.creditsUpdated'));
    } catch (err) {
      setNotice(
        err instanceof AssetRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.somethingWrong'),
      );
    } finally {
      setCreditsSaving(false);
    }
  }, [creditsDraft, selectedWorker, t]);

  const panelClass = [styles.panel, language === 'he' ? styles.panelRtl : ''].filter(Boolean).join(' ');

  const subtitle = businessName?.trim() || null;

  const formatDetail = (value: string | null | undefined, isPhone = false): string => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return t('profile.field.notSet');
    }
    return isPhone ? formatIsraeliPhoneDisplay(trimmed) : trimmed;
  };

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.businessWorkers.title')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={panelClass} dir={textDir}>
          <div className={styles.topBar}>
            <button
              type="button"
              className={styles.topBarBtn}
              onClick={selectedWorker ? backToList : onBack}
              aria-label={selectedWorker ? t('profile.businessWorkers.backToList') : t('profile.hub.back')}
            >
              <PageBackIcon rtl={language === 'he'} />
            </button>
            <div>
              <h2 className={styles.topBarTitle}>
                {selectedWorker ? workerListPrimaryLabel(selectedWorker) : t('profile.businessWorkers.title')}
              </h2>
              {!selectedWorker && subtitle ? (
                <p className={styles.topBarSubtitle}>{subtitle}</p>
              ) : null}
            </div>
            <span className={styles.topBarBtn} aria-hidden />
          </div>

          {loading ? (
            <p className={styles.status} role="status">
              {t('common.loading')}
            </p>
          ) : null}

          {!loading && !selectedWorker ? (
            <>
              <p className={styles.intro}>{t('profile.businessWorkers.listIntro')}</p>
              {error ? (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              ) : null}
              {workers.length === 0 && !error ? (
                <p className={styles.status} role="status">
                  {t('profile.businessWorkers.empty')}
                </p>
              ) : (
                <ul className={styles.list}>
                  {workers.map((worker) => (
                    <li key={worker.id}>
                      <button
                        type="button"
                        className={[
                          styles.listItemBtn,
                          worker.isInactive ? styles.listItemBtnInactive : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => openWorker(worker.id)}
                      >
                        {worker.avatarSrc ? (
                          <ProfileAvatarImage
                            src={worker.avatarSrc}
                            alt=""
                            className={styles.avatar}
                          />
                        ) : (
                          <span className={styles.avatarPlaceholder} aria-hidden>
                            {workerInitials(workerListPrimaryLabel(worker))}
                          </span>
                        )}
                        <div className={styles.listItemMain}>
                          <p className={styles.listItemName}>{workerListPrimaryLabel(worker)}</p>
                          {workerListSecondaryLabel(worker) ? (
                            <p className={styles.listItemEmail}>{workerListSecondaryLabel(worker)}</p>
                          ) : null}
                          <p className={styles.listItemCredits}>
                            {t('profile.businessWorkers.creditsShort', {
                              amount: formatCredits(worker.availableCredits, language),
                            })}
                          </p>
                        </div>
                        <span className={styles.chevron} aria-hidden>
                          <ListChevronIcon rtl={language === 'he'} />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}

          {!loading && selectedWorker ? (
            <div className={styles.detailCard}>
              <div className={styles.detailHeader}>
                <div className={styles.detailAvatarWrap}>
                  {selectedWorker.avatarSrc ? (
                    <ProfileAvatarImage
                      src={selectedWorker.avatarSrc}
                      alt=""
                      className={styles.detailAvatar}
                    />
                  ) : (
                    <span className={styles.avatarPlaceholder} aria-hidden>
                      {workerInitials(workerListPrimaryLabel(selectedWorker))}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.detailAvatarCameraBtn}
                    aria-label={t('profile.businessWorkers.changePhotoAria')}
                    title={t('profile.businessWorkers.changePhoto')}
                    onClick={onAvatarPick}
                    disabled={avatarSaving || creditsSaving}
                  >
                    <WorkerPhotoCameraIcon />
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.detailAvatarFileInput}
                    tabIndex={-1}
                    aria-hidden
                    onChange={onAvatarFileChange}
                  />
                </div>
                <h3 className={styles.detailName}>{workerListPrimaryLabel(selectedWorker)}</h3>
                {selectedWorker.isInactive ? (
                  <p className={styles.detailStatus}>{t('profile.status.inactive')}</p>
                ) : null}
              </div>

              <dl className={styles.detailsList}>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.firstName')}</dt>
                  <dd className={styles.detailValue}>{formatDetail(selectedWorker.firstName)}</dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.lastName')}</dt>
                  <dd className={styles.detailValue}>{formatDetail(selectedWorker.lastName)}</dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.email')}</dt>
                  <dd className={[styles.detailValue, styles.detailValueLtr].join(' ')}>
                    {formatDetail(selectedWorker.email)}
                  </dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{localizeAssetFieldLabel('national_id', t)}</dt>
                  <dd className={[styles.detailValue, styles.detailValueLtr].join(' ')}>
                    {formatDetail(selectedWorker.nationalId)}
                  </dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.dateOfBirth')}</dt>
                  <dd className={styles.detailValue}>
                    {formatDetail(formatProfileDateOfBirthDisplay(selectedWorker.birthDate))}
                  </dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{localizeAssetFieldLabel('position', t)}</dt>
                  <dd className={styles.detailValue}>{formatDetail(selectedWorker.position)}</dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.cellPhone')}</dt>
                  <dd className={[styles.detailValue, styles.detailValueLtr].join(' ')}>
                    {formatDetail(selectedWorker.phone, true)}
                  </dd>
                </div>
                <div className={styles.detailRow}>
                  <dt className={styles.detailLabel}>{t('profile.field.availableCredits')}</dt>
                  <dd className={[styles.detailValue, styles.detailValueLtr].join(' ')}>
                    {formatCredits(selectedWorker.availableCredits, language)}
                  </dd>
                </div>
              </dl>

              <div className={styles.creditsPanel}>
                <p className={styles.creditsPanelTitle}>{t('profile.businessWorkers.editCreditsTitle')}</p>
                {editingCredits ? (
                  <>
                    <label className={styles.detailLabel} htmlFor={creditsInputId}>
                      {t('profile.field.availableCredits')}
                    </label>
                    <input
                      id={creditsInputId}
                      className={styles.creditsInput}
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      value={creditsDraft}
                      onChange={(event) => setCreditsDraft(event.target.value)}
                      disabled={creditsSaving}
                    />
                    <div className={styles.creditsActions}>
                      <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={() => void saveCredits()}
                        disabled={creditsSaving}
                      >
                        {t('profile.businessWorkers.updateCredits')}
                      </button>
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => setEditingCredits(false)}
                        disabled={creditsSaving}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <button type="button" className={styles.primaryBtn} onClick={startEditCredits}>
                    {t('profile.businessWorkers.editCredits')}
                  </button>
                )}
                {notice ? (
                  <p
                    className={
                      notice === t('profile.businessWorkers.creditsUpdated') ||
                      notice === t('profile.businessWorkers.photoUpdated')
                        ? styles.success
                        : styles.error
                    }
                    role="status"
                  >
                    {notice}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {avatarCropFile ? (
            <ProfileAvatarCropPanel
              file={avatarCropFile}
              onCancel={onAvatarCropCancel}
              onSave={(dataUrl) => void onAvatarCropSave(dataUrl)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function WorkerPhotoCameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function PageBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={rtl ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ListChevronIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={rtl ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
